import { useIntl } from 'react-intl';
import { Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/solid';
import {
    SideMenu,
    SideMenuTopBar,
    SelectOptionValue,
} from '@luminalearning/components';
import env from 'src/env/index';
import { useQuery, useReactiveVar } from '@apollo/client';
import { activePractitionerDetails, isMobileNavOpen } from 'src/graphql';
import { locales } from 'src/lang/locales';
import useLanguageChange from 'src/hooks/useLanguageChange';
import { useEffect, useState } from 'react';
import GET_LOCALE from 'src/graphql/queries/intlQuery';
import USER_TOKEN from 'src/graphql/queries/authQuery';
import { loginLuminaOnline } from 'src/services/auth';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';

const SideBarContainer = () => {
    const ssoVersion = localStorage.getItem('ssoVersion');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [avatar, setAvatar] = useState();
    const [isApiPractitioner, setIsApiPractitioner] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const { data: locale } = useQuery(GET_LOCALE);
    const { data: token } = useQuery(USER_TOKEN);
    const intl = useIntl();
    const navigate = useNavigate();
    const changeLanguage = useLanguageChange();
    const practitionerDetails = useReactiveVar(activePractitionerDetails);
    const handleLanguageChange = (value: SelectOptionValue | null) => {
        if (value !== null) {
            changeLanguage(value.id as string);
        }
    };

    useEffect(() => {
        if (practitionerDetails) {
            setName(`${practitionerDetails.firstName} ${practitionerDetails.lastName}`);
            setEmail(practitionerDetails.email);
            setIsApiPractitioner(practitionerDetails.isApiEnabled === true);
        }
    }, [practitionerDetails]);

    useEffect(() => {
        if (token) {
            setAvatar(token?.picture || undefined);
        }
    }, [token]);

    const navigateToSupportPage = async () => {
        if (practitionerDetails) {
            setIsNavigating(true);
            await loginLuminaOnline(practitionerDetails.id);
            window.location.href = `${env.luminaOnlineUrl}/support`;
            setIsNavigating(false);
        }
    };

    return (
        <div className='h-screen sticky top-0 overflow-y-auto z-10'>
            <Transition
                show={useReactiveVar(isMobileNavOpen)}
                enter='transition ease-linear duration-300 transform'
                enterFrom='-translate-x-full'
                enterTo='translate-x-0'
                leave='transition ease-linear duration-300 transform'
                leaveFrom='translate-x-0'
                leaveTo='-translate-x-full'
                className='z-30 fixed top-0 xl:hidden'
                as='div'
            >
                <SideMenu className='w-auto'>
                    <button
                        className='fixed top-6 right-6 h-6 w-6 z-50 text-ui-form-field-border'
                        onClick={() => isMobileNavOpen(false)}
                        aria-label={intl.formatMessage({
                            id: 'LS_MODAL_CLOSE_BUTTON',
                            defaultMessage: 'Close',
                        })}
                    >
                        <XIcon />
                    </button>
                    <SideMenuTopBar
                        handleLogin={() => {
                            // eslint-disable-next-line max-len
                            window.location.href = `${ssoVersion === '2' ? env.ssoV2Url : env.ssoV1Url}/login?returnUrl=${encodeURIComponent(window.location.href)}`;
                        }}
                        handleSignOut={() => navigate('/logout')}
                        handleProfileClick={() => {
                            window.location.href = `${ssoVersion === '2' ? env.ssoV2Url : env.ssoV1Url}/portal/profile`;
                        }}
                        handleSupportClick={() => {
                            if (!isNavigating) {
                                navigateToSupportPage();
                            }
                        }}
                        language={{
                            id: locale.appLocale,
                            value: (locales[locale.appLocale]?.localName || locales[locale.appLocale].englishName),
                        }}
                        userName={name}
                        userPicture={avatar}
                        userEmail={email}
                        languages={
                            Object.keys(locales).map(
                                (localeKey) => ({
                                    id: localeKey,
                                    value: (locales[localeKey]?.localName || locales[localeKey].englishName),
                                    isDisabled: false,
                                })
                            )
                        }
                        setLanguage={handleLanguageChange}
                        className='fixed top-0 w-80'
                    />
                    <Sidebar isMobile={true} isApiPractitioner={isApiPractitioner} />
                </SideMenu>
            </Transition>

            <div className='hidden lg:block xl:block'>
                <SideMenu className='h-fit w-auto'>
                    <Sidebar isApiPractitioner={isApiPractitioner} />
                </SideMenu>
            </div>
        </div>
    );
};

export default SideBarContainer;
